import { Fragment, useMemo } from 'react';
import type { LlmChatAttachmentDto, LlmChatEntityReferenceDto } from '@paladin-monorepo/common';
import { parseSegments, type Segment } from '@/lib/llm-chat-reference-segments';
import { MarkdownContent } from './MarkdownContent';
import { LlmChatMessageAttachment } from './LlmChatMessageAttachment';
import { LlmChatEntityReference } from './LlmChatEntityReference';

function resolveAttachment(
  segment: Extract<Segment, { type: 'attachment' }>,
  attachments: LlmChatAttachmentDto[],
): LlmChatAttachmentDto | undefined {
  if (segment.attachmentId) {
    return attachments.find((attachment) => attachment.id === segment.attachmentId);
  }
  if (segment.index != null && segment.index >= 1) {
    return attachments[segment.index - 1];
  }
  return undefined;
}

interface LlmChatMarkdownContentProps {
  content: string;
  sessionId: string;
  attachments?: LlmChatAttachmentDto[];
  references?: LlmChatEntityReferenceDto[];
  className?: string;
}

export function LlmChatMarkdownContent({
  content,
  sessionId,
  attachments = [],
  references = [],
  className,
}: LlmChatMarkdownContentProps) {
  const segments = useMemo(() => parseSegments(content, references), [content, references]);

  return (
    <div className={className}>
      {segments.map((segment, index) => {
        if (segment.type === 'text') {
          if (!segment.value.trim()) return null;
          return <MarkdownContent key={index} content={segment.value} />;
        }

        if (segment.type === 'reference') {
          return <LlmChatEntityReference key={index} reference={segment.reference} />;
        }

        const attachment = resolveAttachment(segment, attachments);
        if (!attachment) {
          return (
            <span key={index} className="text-xs text-muted-foreground">
              {segment.raw}
            </span>
          );
        }

        return (
          <Fragment key={index}>
            <LlmChatMessageAttachment
              sessionId={sessionId}
              attachment={attachment}
              className="my-1 block"
            />
          </Fragment>
        );
      })}
    </div>
  );
}
